import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  createReportSchedule,
  fetchReportScheduleById,
  updateReportSchedule,
} from './api/reportsApi'
import ReportScheduleForm, { normalizeScheduleForForm } from './components/ReportScheduleForm'

export default function ReportScheduleEntryScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const scheduleId = Number(searchParams.get('id') || 0)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [scheduleData, setScheduleData] = useState(location.state?.schedule || null)

  const isEditMode = Number.isFinite(scheduleId) && scheduleId > 0
  const initialValue = useMemo(() => normalizeScheduleForForm(scheduleData), [scheduleData])

  useEffect(() => {
    let active = true
    async function loadSchedule() {
      if (!isEditMode || scheduleData) return
      setLoading(true)
      setLoadError('')
      try {
        const row = await fetchReportScheduleById(scheduleId)
        if (!active) return
        setScheduleData(row)
      } catch (requestError) {
        if (!active) return
        setLoadError(requestError?.response?.data?.detail?.message || 'Failed to load schedule.')
      } finally {
        if (active) setLoading(false)
      }
    }
    loadSchedule()
    return () => {
      active = false
    }
  }, [isEditMode, scheduleData, scheduleId])

  async function handleSubmit(payload) {
    setSaving(true)
    try {
      if (isEditMode) {
        await updateReportSchedule(scheduleId, payload)
      } else {
        await createReportSchedule(payload)
      }
      window.dispatchEvent(new Event('report-schedules-updated'))
      navigate('/reports/schedules/list')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="px-8 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-headline text-5xl font-extrabold text-on-surface tracking-tight mb-2">
            {isEditMode ? 'Edit Schedule' : 'Create Schedule'}
          </h1>
            <p className="text-on-surface-variant text-lg max-w-xl">Configure report schedule dispatch settings.</p>
        </div>

        {/* <div>
            <h1 className="font-headline text-5xl font-extrabold text-on-surface tracking-tight mb-2">
              Global Reports Hub
            </h1>
            <p className="text-on-surface-variant text-lg max-w-xl">
              Centralized intelligence center for Bridge Enterprise using live report data.
            </p>
          </div> */}
        <button
          type="button"
          className="rounded-lg border border-outline-variant/30 px-4 py-2 text-sm font-semibold text-on-surface"
          onClick={() => navigate('/reports/schedules/list')}
        >
          Back to List
        </button>
      </div>

      {loadError ? <p className="mb-3 text-sm text-error">{loadError}</p> : null}
      {loading ? (
        <p className="text-sm text-on-surface-variant">Loading schedule...</p>
      ) : (
        <ReportScheduleForm
          initialValue={initialValue}
          heading={isEditMode ? 'Edit Schedule' : 'Create Schedule'}
          submitLabel={isEditMode ? 'Update Schedule' : 'Create Schedule'}
          saving={saving}
          onCancel={() => navigate('/reports/schedules/list')}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  )
}
